import React from 'react';
import {
  Box, Container, Grid, Paper, Typography, FormControl, InputLabel, Select,
  MenuItem, Table, TableHead, TableBody, TableRow, TableCell, Skeleton, Chip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend,
} from 'recharts';
import { ga4API } from '../services/api';
import Panel from './crt/Panel';
import SectionTitle from './crt/SectionTitle';
import { useUrlState, useUrlIntState } from '../hooks/useUrlState';

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();
const fmtPct = (n: number | null | undefined) => `${((n ?? 0) * 100).toFixed(1)}%`;
const fmtSec = (n: number | null | undefined) => {
  const s = Math.round(n ?? 0);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};

const GA4Dashboard: React.FC = () => {
  const [site] = useUrlState('ga4_site', 'spoutlore');
  const [days, setDays] = useUrlIntState('ga4_days', 30);

  const { data: daily, isLoading: dailyLoading } = useQuery({
    queryKey: ['ga4-daily', site, days],
    queryFn: () => ga4API.daily(site, days),
    staleTime: 5 * 60_000,
  });
  const { data: sources, isLoading: srcLoading } = useQuery({
    queryKey: ['ga4-sources', site, days],
    queryFn: () => ga4API.sources(site, days, 25),
    staleTime: 5 * 60_000,
  });
  const { data: topPages, isLoading: pgLoading } = useQuery({
    queryKey: ['ga4-pages', site, days],
    queryFn: () => ga4API.topPages(site, days, 25),
    staleTime: 5 * 60_000,
  });

  // KPI totals across the window
  const dailyRows = daily?.rows || [];
  const totalSessions = dailyRows.reduce((a: number, r: any) => a + (r.sessions || 0), 0);
  const totalPageViews = dailyRows.reduce((a: number, r: any) => a + (r.page_views || 0), 0);
  const totalUsers = dailyRows.reduce((a: number, r: any) => a + (r.active_users || 0), 0);
  const totalNewUsers = dailyRows.reduce((a: number, r: any) => a + (r.new_users || 0), 0);
  const avgBounce = dailyRows.length ?
    dailyRows.reduce((a: number, r: any) => a + (r.bounce_rate || 0), 0) / dailyRows.length : 0;
  const avgSession = dailyRows.length ?
    dailyRows.reduce((a: number, r: any) => a + (r.avg_session_seconds || 0), 0) / dailyRows.length : 0;

  const sourceRows = sources?.rows || [];
  // megaphone/podcast row stands out — that's the bulk-footer attribution
  const podcastSessions = sourceRows
    .filter((r: any) => r.source === 'megaphone' || r.medium === 'podcast')
    .reduce((a: number, r: any) => a + (r.sessions || 0), 0);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4">GOOGLE ANALYTICS</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Site</InputLabel>
          <Select label="Site" value={site} disabled>
            <MenuItem value="spoutlore">Spout Lore</MenuItem>
            {/* Mall Brats GA4 not configured yet — add when property exists */}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Window</InputLabel>
          <Select label="Window" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={180}>Last 180 days</MenuItem>
            <MenuItem value={365}>Last 365 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'SESSIONS',         value: fmt(totalSessions) },
          { label: 'PAGE VIEWS',       value: fmt(totalPageViews) },
          { label: 'ACTIVE USERS',     value: fmt(totalUsers) },
          { label: 'NEW USERS',        value: fmt(totalNewUsers) },
          { label: 'AVG SESSION',      value: fmtSec(avgSession) },
          { label: 'BOUNCE RATE',      value: fmtPct(avgBounce) },
        ].map((kpi) => (
          <Grid size={{ xs: 6, md: 2 }} key={kpi.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">{kpi.label}</Typography>
              <Typography variant="h5" sx={{ mt: 1 }}>
                {dailyLoading ? <Skeleton width={80} /> : kpi.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Megaphone attribution callout */}
      {podcastSessions > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderLeft: '4px solid #00ff41' }}>
          <Typography variant="caption">PODCAST → WEBSITE ATTRIBUTION (utm_source=megaphone)</Typography>
          <Typography variant="h5" sx={{ mt: 1 }}>
            {fmt(podcastSessions)} sessions from podcast apps
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Listeners clicking the "Read the 📝 Episode Details" link in episode descriptions.
          </Typography>
        </Paper>
      )}

      {/* Daily timeline */}
      <Panel label="DAILY TIMELINE" sx={{ mb: 3 }}>
        <SectionTitle>{`SESSIONS / PAGE VIEWS / USERS — LAST ${days} DAYS`}</SectionTitle>
        {dailyLoading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyRows}>
              <CartesianGrid stroke="#1e3a1e" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#5aaa5a" tick={{ fontSize: 10 }}
                     tickFormatter={(d: string) => d.slice(5)} />
              <YAxis stroke="#5aaa5a" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#0f0f0f', border: '1px solid #1e3a1e' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="sessions"     stroke="#00ff41" fill="#00ff41" fillOpacity={0.2} />
              <Area type="monotone" dataKey="page_views"   stroke="#00ffff" fill="#00ffff" fillOpacity={0.15} />
              <Area type="monotone" dataKey="active_users" stroke="#ffb000" fill="#ffb000" fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <Grid container spacing={3}>
        {/* Top sources */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="TOP TRAFFIC SOURCES">
            <SectionTitle>SOURCE / MEDIUM (BY SESSIONS)</SectionTitle>
            {srcLoading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SOURCE / MEDIUM</TableCell>
                      <TableCell align="right">SESSIONS</TableCell>
                      <TableCell align="right">USERS</TableCell>
                      <TableCell align="right">PV</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sourceRows.map((r: any, i: number) => {
                      const isPodcast = r.source === 'megaphone' || r.medium === 'podcast';
                      return (
                        <TableRow key={`${r.source}/${r.medium}/${i}`} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                              {r.source}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {r.medium}{isPodcast && ' ★'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmt(r.sessions)}</TableCell>
                          <TableCell align="right">{fmt(r.active_users)}</TableCell>
                          <TableCell align="right">{fmt(r.page_views)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Panel>
        </Grid>

        {/* Top pages */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="TOP PAGES">
            <SectionTitle>PAGE PATH (BY PAGE VIEWS)</SectionTitle>
            {pgLoading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>PAGE</TableCell>
                      <TableCell align="right">VIEWS</TableCell>
                      <TableCell align="right">SESSIONS</TableCell>
                      <TableCell align="right">USERS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(topPages?.rows || []).map((r: any, i: number) => (
                      <TableRow key={`${r.page_path}/${i}`} hover>
                        <TableCell sx={{ maxWidth: 380 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                                                            wordBreak: 'break-all' }}>
                            {r.page_path}
                          </Typography>
                          {r.page_title && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {r.page_title.slice(0, 80)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{fmt(r.page_views)}</TableCell>
                        <TableCell align="right">{fmt(r.sessions)}</TableCell>
                        <TableCell align="right">{fmt(r.active_users)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Container>
  );
};

export default GA4Dashboard;
